/**
 * Native chrome (status bar + splash screen) helpers.
 *
 * All exports are no-ops on web. Lazy-imports the Capacitor plugins so the
 * web bundle never pulls native code.
 */
import { Capacitor } from "@capacitor/core";

export async function configureNativeChrome(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Dark icons on cream background (matches NativeHome top nav).
    await StatusBar.setStyle({ style: Style.Dark });
    if (Capacitor.getPlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#FAECE7" });
    }
  } catch (err) {
    console.error("[nativeChrome] StatusBar config failed:", err);
  }

  try {
    const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
    // Belt-and-braces: also set at runtime in case config wasn't synced.
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    await Keyboard.setScroll({ isDisabled: false });
    // After the keyboard hides, iOS sometimes leaves the webview scrolled.
    // Force the document & window back to (0,0) so fixed/absolute layouts
    // (GRWM, EditItemSheet, etc.) render correctly on the next screen.
    Keyboard.addListener("keyboardDidHide", () => {
      try {
        window.scrollTo(0, 0);
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      } catch {}
    });
  } catch (err) {
    console.error("[nativeChrome] Keyboard config failed:", err);
  }
}

export async function hideSplash(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch (err) {
    console.error("[nativeChrome] SplashScreen.hide failed:", err);
  }
}
